<template>
  <div class="container mx-auto flex flex-col items-center">
    <div
      id="login-box"
      class="m-4 flex flex-col items-center bg-white shadow rounded w-full max-w-screen-md"
    >
      <slot></slot>

      <!-- Tabs -->
      <div class="flex w-full border-b border-gray-200">
        <button
          class="flex-1 py-3 text-sm font-medium transition-colors focus:outline-none"
          :class="
            activeTab === 'login'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-primary'
          "
          @click="activeTab = 'login'"
          type="button"
        >
          Login
        </button>
        <button
          class="flex-1 py-3 text-sm font-medium transition-colors focus:outline-none"
          :class="
            activeTab === 'create'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-primary'
          "
          @click="activeTab = 'create'"
          type="button"
        >
          {{ props.createAccountTitle || "Create Accountsssssss" }}
        </button>
      </div>

      <div class="p-4 flex flex-col w-full">
        <h2 class="text-xl text-primary font-medium text-center mb-2">{{ appConfig.title }}</h2>

        <!-- LOGIN TAB -->
        <div v-if="activeTab === 'login'">
          <Form @submit="submitLogin" v-if="!state.loading">
            <Input
              class="flex flex-grow"
              name="email"
              label="Email Address"
              type="email"
              autocomplete="email"
            />
            <Input
              class="flex flex-grow"
              name="password"
              label="Password"
              type="password"
              autocomplete="current-password"
              v-if="!state.reset"
            />
            <p
              class="p-2 text-sm text-primary italic hover:underline cursor-pointer"
              @click="state.reset = !state.reset"
            >
              {{ state.reset ? "Enter Password" : "Forgotten Password?" }}
            </p>
            <button class="primary" type="submit">
              {{ state.reset ? "Send Reset Password Email" : "Login" }}
            </button>
          </Form>
          <Loading message="Checking credentials" v-if="state.loading" />
        </div>

        <!-- CREATE ACCOUNT TAB -->
        <div v-if="activeTab === 'create'">
          <Form @submit="submitCreate" v-if="!state.loading">
            <Input
              class="flex flex-grow"
              name="firstName"
              label="First Name"
              type="text"
              autocomplete="given-name"
            />
            <Input
              class="flex flex-grow"
              name="lastName"
              label="Last Name"
              type="text"
              autocomplete="family-name"
            />
            <Input
              class="flex flex-grow"
              name="email"
              label="Email Address"
              type="email"
              autocomplete="email"
            />
            <Input
              class="flex flex-grow"
              name="password"
              label="Password"
              type="password"
              autocomplete="new-password"
            />
            <Input
              class="flex flex-grow"
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              autocomplete="new-password"
            />

            <!-- GDPR Consent -->
            <div class="flex items-start gap-2 mt-3 px-2">
              <input
                id="gdpr-checkbox"
                type="checkbox"
                class="mt-1 accent-primary cursor-pointer"
                v-model="state.gdprAccepted"
              />
              <label for="gdpr-checkbox" class="text-sm text-gray-700 leading-snug cursor-pointer select-none">
                I agree to the processing of my personal data by
                <strong>{{ appConfig.title }}</strong>
              </label>
              <button
                type="button"
                class="flex-shrink-0 text-primary hover:text-primary/70 focus:outline-none"
                aria-label="Data processing information"
                @click="state.showGdprModal = true"
              >
                <font-awesome-icon :icon="['fas', 'circle-info']" />
              </button>
            </div>

            <button class="primary" type="submit">Create Account</button>
          </Form>
          <Loading message="Creating account" v-if="state.loading" />
        </div>
      </div>
    </div>
  </div>

  <!-- GDPR Modal -->
  <teleport to="body">
    <div
      v-if="state.showGdprModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gdpr-modal-title"
      @click.self="state.showGdprModal = false"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 id="gdpr-modal-title" class="text-base font-semibold text-primary">
            Data Processing Notice
          </h3>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600 focus:outline-none text-lg leading-none"
            aria-label="Close"
            @click="state.showGdprModal = false"
          >
            &times;
          </button>
        </div>

        <!-- Modal Body -->
        <div class="px-5 py-4 text-sm text-gray-700 space-y-3">
          <p>
            To create your account, <strong>{{ appConfig.title }}</strong> requires the following
            personal information:
          </p>
          <ul class="list-disc list-inside space-y-1 pl-2">
            <li><strong>First Name &amp; Last Name</strong> &mdash; to identify your account</li>
            <li><strong>Email Address</strong> &mdash; to allow you to log in and receive account communications</li>
          </ul>
          <p>
            By checking the consent box, you confirm that you are happy for
            <strong>{{ appConfig.title }}</strong> to collect and process your personal data
            for the purpose of managing your account.
          </p>
          <p>
            You may withdraw your consent at any time by contacting us or requesting that your
            account be deleted.
          </p>
        </div>

        <!-- Modal Footer -->
        <div class="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            class="primary"
            @click="state.showGdprModal = false"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { defineProps, defineExpose, reactive, ref } from "vue";
import { Input } from "../Inputs";
import { Form } from "../Form";
import { Loading } from "../Loading";

const props = defineProps({
  auth: Object,
  appConfig: Object,
  createAccountTitle: String,
  requestPasswordReset: Function,
});

const emit = defineEmits(["login", "reset", "error", "create-account"]);

const activeTab = ref("login");

const state = reactive({
  loading: !!localStorage.getItem("expectSignIn"),
  reset: false,
  gdprAccepted: false,
  showGdprModal: false,
});

async function submitLogin(data) {
  state.loading = true;

  if (state.reset) {
    try {
      if (!props.requestPasswordReset) {
        throw new Error("Password reset is not configured.");
      }
      await props.requestPasswordReset(data.email);
      emit("reset");
    } catch (err) {
      emit("error", err);
    } finally {
      state.loading = false;
    }
    return;
  }

  props.auth
    .signInWithEmailAndPassword(data.email, data.password)
    .then((userCredential) => {
      emit("login", userCredential);
    })
    .catch((err) => {
      console.error("ERROR", err);
      state.loading = false;
      emit("error", err);
    });
}

function submitCreate(data) {
  if (!state.gdprAccepted) {
    emit("error", {
      code: "auth/gdpr-not-accepted",
      message: "You must accept the data processing policy to create an account.",
    });
    return;
  }

  if (data.password !== data.confirmPassword) {
    emit("error", { code: "auth/passwords-do-not-match", message: "Passwords do not match." });
    return;
  }

  state.loading = true;
  emit("create-account", {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    password: data.password,
  });
}

// Allow the parent to clear the loading state when an async
// create-account flow it owns (auth + callable) fails.
function reset() {
  state.loading = false;
}

defineExpose({ reset });
</script>
